import {
  BannerCopyVariant,
  CMPSettingsDto,
  ConsentManagementVariant,
  DismissalButtonCopyVariant,
} from "../../../../common/service/server/dto/CMPSettingsDto";

export type ConsentBannerFormValues = Partial<CMPSettingsDto> & {
  // Florida is edited like a region group but saved as a custom state override.
  usConsentManagementVariantFL?: ConsentManagementVariant;
};

/** The region groups the form shows a banner choice for, beyond the ones it always shows. */
export interface ShownBannerRegions {
  california: boolean;
  childrenConsentStates: boolean;
  canada: boolean;
  eeaUkCh: boolean;
  // The org has global consent and its section is turned on.
  globalConsent: boolean;
}

/** The shown regions still on "Choose a banner". Publishing to either environment needs a choice for each. */
export const regionsWithoutABanner = (
  values: ConsentBannerFormValues,
  shown: ShownBannerRegions,
): string[] =>
  [
    shown.california && !values.usConsentManagementVariantCA && "California",
    !values.usConsentManagementVariantFL && "Florida",
    !values.usConsentManagementVariantUSP && "privacy states",
    !values.usConsentManagementVariantNonUSP && "non-privacy states",
    shown.childrenConsentStates &&
      !values.usConsentManagementVariantMinors &&
      "children consent states",
    shown.canada && !values.canadaConsentManagementVariant && "Canada",
    shown.eeaUkCh && !values.eeaukConsentManagementVariant && "EEA/UK/CH",
    shown.globalConsent && !values.globalConsentManagementVariantDefault && "global consent",
  ].filter((region): region is string => Boolean(region));

/**
 * The form values to publish. A production publish leaves any setting sent as null unchanged, so settings that were
 * cleared (by editing, or by copying in the other environment's settings) go out as explicit empty values: blank
 * custom text and the default button copy, plus no global consent banner or overrides when those are gone but the
 * environment's saved settings still have them. Global consent that was never on stays null, so publishing doesn't
 * record a change nobody made. Without saved settings (region previews), cleared values always go out explicitly.
 */
export const withClearedSettings = (
  values: ConsentBannerFormValues,
  {
    globalConsentTurnedOff,
    saved,
  }: {
    globalConsentTurnedOff: boolean;
    // This environment's saved settings, as the form loaded them.
    saved?: ConsentBannerFormValues;
  },
): ConsentBannerFormValues => {
  const savedGlobalDefaultOn =
    !saved ||
    (saved.globalConsentManagementVariantDefault != null &&
      saved.globalConsentManagementVariantDefault !== ConsentManagementVariant.NONE);
  const savedGlobalOverrides = !saved || (saved.globalConsentManagementVariants?.length ?? 0) > 0;

  // Turned off: clear what's saved, or send back what's saved (already off) unchanged.
  const globalConsentManagementVariantDefault = globalConsentTurnedOff
    ? savedGlobalDefaultOn
      ? ConsentManagementVariant.NONE
      : saved?.globalConsentManagementVariantDefault
    : values.globalConsentManagementVariantDefault;
  const globalOverrides = globalConsentTurnedOff
    ? saved?.globalConsentManagementVariants
    : values.globalConsentManagementVariants;
  const globalConsentManagementVariants =
    savedGlobalOverrides && (globalConsentTurnedOff || globalOverrides == null)
      ? []
      : globalOverrides;

  return {
    ...values,
    bannerTextOptInEn: values.bannerTextOptInEn || "",
    bannerTextOptOutEn: values.bannerTextOptOutEn || "",
    bannerPreferencesDisclaimerTextEn: values.bannerPreferencesDisclaimerTextEn || "",
    bannerCopyVariant: values.bannerCopyVariant || BannerCopyVariant.DEFAULT,
    bannerDismissalButtonCopyVariant:
      values.bannerDismissalButtonCopyVariant || DismissalButtonCopyVariant.DEFAULT,
    globalConsentManagementVariantDefault,
    globalConsentManagementVariants,
  };
};
